<template>
    <Layout>
        <PageHeader :title="$t('module.notificationIcon')" :items="breadcrumbs" />
        <div class="card">
            <div class="card-body">
                <div class="row mb-3">
                    <div class="col-lg-4 mb-2 mb-lg-0">
                        <b-input-group>
                            <b-form-input v-model.trim="searchText" type="search" :placeholder="$t('label.search')"
                                @keyup.enter="applySearch" />
                            <b-input-group-append><b-button variant="info" @click="applySearch">
                                    <i class="mdi mdi-magnify"></i>
                                </b-button></b-input-group-append>
                        </b-input-group>
                    </div>
                    <div class="col-lg-8 d-flex justify-content-lg-end">
                        <b-button variant="info" @click="openForm()">
                            <i class="mdi mdi-plus mr-1"></i>{{ $t('label.createX', [$t('module.notificationIcon')]) }}
                        </b-button>
                    </div>
                </div>
                <div v-if="!status.list" class="d-flex justify-content-center p-5"><b-spinner variant="info" /></div>
                <template v-else>
                    <b-table :items="tableRows" :fields="fields" show-empty responsive no-local-sorting
                        @sort-changed="changeSort">
                        <template #cell(url)="{ item }">
                            <img v-if="item.url" :src="item.url" :alt="item.name" class="notification-icon-preview">
                            <span v-else>-</span>
                        </template>
                        <template #cell(isActive)="{ item }">
                            <b-form-checkbox :checked="item.isActive" switch
                                :disabled="!status.save || Boolean(item.deletedAt)"
                                @change="changeStatus(item, $event)">
                                {{ item.isActive ? $t('label.active') : $t('label.inactive') }}
                            </b-form-checkbox>
                        </template>
                        <template #cell(actions)="{ item }">
                            <template v-if="!item.deletedAt">
                                <a href="javascript:void(0);" class="text-info" v-b-tooltip.hover
                                    :title="$t('label.updateX', [$t('module.notificationIcon')])"
                                    @click.stop="openForm(item)">
                                    <i class="mdi mdi-pencil-outline font-size-18"></i>
                                </a>
                                <a href="javascript:void(0);" class="text-danger ml-2" v-b-tooltip.hover
                                    :title="$t('label.deleteNotificationIcon')"
                                    @click.stop="confirmDelete(item, false)">
                                    <i class="mdi mdi-delete-outline font-size-18"></i>
                                </a>
                            </template>
                            <a v-else href="javascript:void(0);" class="text-danger" v-b-tooltip.hover
                                :title="$t('label.permanentlyDeleteNotificationIcon')"
                                @click.stop="confirmDelete(item, true)">
                                <i class="mdi mdi-delete-forever font-size-18"></i>
                            </a>
                        </template>
                    </b-table>
                    <div v-if="pagination.total" class="d-flex justify-content-between align-items-center mt-3">
                        <small class="text-muted">{{ $tc('label.results', pagination.total, [pagination.total])
                            }}</small>
                        <b-pagination v-model="currentPage" :total-rows="pagination.total"
                            :per-page="searchCriteria.pageSize" class="mb-0" @change="pageNavigation" />
                    </div>
                </template>
            </div>
        </div>
        <b-modal id="notification-icon-form"
            :title="$t(icon.uuid ? 'label.updateX' : 'label.createX', [$t('module.notificationIcon')])" size="lg"
            hide-footer hide-header-close centered @hidden="closeForm">
            <b-container fluid>
                <NotificationIconForm v-if="formVisible" :icon-u-u-i-d="icon.uuid" :search-criteria="searchCriteria"
                    @done="closeForm" />
            </b-container>
        </b-modal>
    </Layout>
</template>

<script>
import { mapState } from 'vuex';
import Layout from '@/views/layouts/main';
import PageHeader from '@/components/page-header';
import i18n from '@/i18n';
import { notificationIconMethods } from '@/state/api';
import { NOTIFICATION_ICON_MODULE } from '@/state/api/notificationIcon.state';
import NotificationIconForm from '@/views/pages/admin/notificationIcon/form';

const defaultCriteria = () => ({
    pageNumber: 1, 
    pageSize: 15, 
    searchArray: [], 
    isActive: '', 
    sortBy: 'created_at', 
    orderBy: 'desc',
});

export default {
    name: 'NotificationIconListing',
    page: { title: i18n.t('module.notificationIcon') },
    components: { Layout, PageHeader, NotificationIconForm },
    data() {
        return {
            searchText: '', 
            currentPage: 1, 
            formVisible: false, icon: { uuid: '' },
            searchCriteria: defaultCriteria(),
            breadcrumbs: [
                { text: i18n.t('module.communication'), href: '/communications' },
                { text: i18n.t('module.notificationIcon'), active: true },
            ],
            fields: [
                { key: 'url', label: i18n.t('label.image'), sortable: false },
                { key: 'name', label: i18n.t('label.name'), sortable: true },
                { key: 'isActive', label: i18n.t('label.status'), sortable: false, class: 'text-nowrap' },
                { key: 'updatedAt', label: i18n.t('label.updatedAt'), sortable: true, class: 'text-nowrap' },
                { key: 'actions', label: i18n.t('label.action'), sortable: false, class: 'text-nowrap' },
            ],
        };
    },
    computed: {
        ...mapState(NOTIFICATION_ICON_MODULE, ['status']),
        tableRows() {
            return this.$store.state.api.notificationIcon.list.data || [];
        },
        pagination() {
            return this.$store.state.api.notificationIcon.list.pagination || {};
        },
    },
    created() {
        this.loadIcons();
    },
    methods: {
        ...notificationIconMethods,
        loadIcons() {
            return this.getAllNotificationIcon({ filter: this.searchCriteria });
        },
        applySearch() {
            this.currentPage = 1;
            this.searchCriteria = {
                ...this.searchCriteria, pageNumber: 1,
                searchArray: this.searchText ? [this.searchText] : []
            };
            this.loadIcons();
        },
        pageNavigation(pageNumber) {
            this.currentPage = pageNumber;
            this.searchCriteria = { ...this.searchCriteria, pageNumber };
            this.loadIcons();
        },
        changeSort({ sortBy, sortDesc }) {
            const apiFields = { name: 'name', updatedAt: 'updated_at' };
            if (!apiFields[sortBy]) return;
            this.searchCriteria = {
                ...this.searchCriteria,
                pageNumber: 1,
                sortBy: apiFields[sortBy],
                orderBy: sortDesc ? 'desc' : 'asc'
            };
            this.currentPage = 1;
            this.loadIcons();
        },
        openForm(icon = null) {
            this.icon = { uuid: icon?.uuid || '' };
            this.formVisible = true;
            this.$nextTick(() => this.$bvModal.show('notification-icon-form'));
        },
        closeForm() {
            this.$bvModal.hide('notification-icon-form');
            this.formVisible = false;
            this.icon = { uuid: '' };
        },
        changeStatus(icon, isActive) {
            return this.updateNotificationIconStatus({ uuid: icon.uuid, isActive, filter: this.searchCriteria });
        },
        async confirmDelete(icon, permanent) {
            const confirmed = await this.$bvModal.msgBoxConfirm(
                permanent ? this.$t('label.permanentDeleteWarning', [icon.name])
                    : this.$t('label.confirmDeleteX', [icon.name]),
                {
                    title: this.$t(permanent ? 'label.confirmPermanentDelete' : 'label.confirmDeleteNotificationIcon'),
                    okTitle: this.$t('label.delete'), okVariant: 'danger', cancelTitle: this.$t('label.cancel'),
                    cancelVariant: 'outline-secondary', centered: true
                },
            );
            if (!confirmed) return;
            const action = permanent ? this.forceDeleteNotificationIcon : this.deleteNotificationIcon;
            await action({ uuid: icon.uuid, filter: this.searchCriteria });
        },
    },
};
</script>

<style scoped>
.notification-icon-preview {
    width: 40px;
    height: 40px;
    object-fit: contain;
}
</style>
